import type { ProductCardData, ProductVariantStock } from "./product-card";

/**
 * Returns true when a single variant should be considered out-of-stock.
 *
 * - sellWhenOutOfStock === true: always available.
 * - tracksInventory === false: always available (sell even when OOS).
 * - minimumStockLevel === 0 (default): legacy behaviour — only OOS when
 *   tracksInventory is explicitly true.
 * - Unknown quantity is treated as available, never as OOS.
 *
 * This is the canonical rule — the grid card and the product detail view must
 * both use it. They previously carried separate copies that disagreed, which is
 * how EPD-10274 shipped.
 */
export function isVariantOutOfStock(
  variant: ProductVariantStock,
  tracksInventory?: boolean,
  minimumStockLevel = 0,
): boolean {
  if (variant.sellWhenOutOfStock === true) return false;
  if (tracksInventory === false) return false;
  if (minimumStockLevel === 0 && tracksInventory !== true) return false;
  if (variant.inventoryQuantity == null) return false;
  return variant.inventoryQuantity <= minimumStockLevel;
}

/**
 * Returns true when a product should be considered out-of-stock in the UI.
 *
 * Prefers per-variant stock: a product is out of stock only when *every* variant
 * is. `totalInventory` is Shopify's signed sum across variants, so a single
 * oversold variant (negative quantity) can cancel out siblings that hold real
 * stock and black out a product that is genuinely orderable — e.g. Sage at
 * -2546 against Sand 1615 + Carbon Black 1217 + Mocha 435 summing to just 721
 * (EPD-10274).
 *
 * Falls back to `totalInventory` when no variant stock is available (Shopify
 * list payloads), preserving the previous behaviour for those paths:
 * - tracksInventory === false: always available.
 * - minimumStockLevel > 0: OOS at or below the threshold, regardless of whether
 *   tracksInventory is explicitly true (covers catalog products).
 * - minimumStockLevel === 0: only OOS when tracksInventory is explicitly true
 *   and inventory is zero or less.
 */
export function isProductOutOfStock(
  product: Pick<
    ProductCardData,
    "tracksInventory" | "totalInventory" | "variants"
  >,
  minimumStockLevel = 0,
): boolean {
  if (product.tracksInventory === false) return false;

  const { variants } = product;
  if (variants?.length && variants.some((v) => v.inventoryQuantity != null)) {
    return variants.every((v) =>
      isVariantOutOfStock(v, product.tracksInventory, minimumStockLevel),
    );
  }

  if (product.totalInventory == null) return false;
  if (minimumStockLevel > 0) return product.totalInventory <= minimumStockLevel;
  return product.tracksInventory === true && product.totalInventory <= 0;
}
